import { Tag } from 'lucide-react';
import { CARD_COUNTER_ICONS, CARD_MARKER_ICONS, COUNTER_LABELS, MARKER_LABELS } from './cardLabels';

/**
 * Werte einer Karteninstanz für Brettkarte, schwebende Vorschau und Langdruck-Sheet
 * (Spec 2026-10-21 §14): Stärke/Widerstandskraft mit eingerechneten Countern und die übrigen
 * Counter und Marken als Badge-Einträge. Alle Werte stammen von der sichtbaren Seite
 * (`visibleFace`); der Hauptwert (`primaryCounter`) ist der Marken-Typ unten rechts auf der Karte
 * und richtet sich nur nach den gedruckten Werten dieser Seite.
 */

const PT_COUNTERS = ['+1/+1', '-1/-1'];
const isInteger = (value) => /^-?\d+$/.test(value);

const str = (value) => (typeof value === 'string' ? value.trim() : '');

/** Werte der sichtbaren Seite (faces[transformedFace] ?? faces[0]), ohne faces die Zusammenfassung aus scryfallData; alle Felder Strings. */
export function visibleFace(card) {
  const data = card?.scryfallData || {};
  const faces = Array.isArray(data.faces) ? data.faces : [];
  const face = (card?.transformedFace === 1 ? faces[1] : null) ?? faces[0];
  if (face) {
    return {
      name: str(face.name),
      manaCost: str(face.manaCost),
      typeLine: str(face.typeLine),
      power: str(face.power),
      toughness: str(face.toughness),
      loyalty: str(face.loyalty),
      defense: str(face.defense),
    };
  }
  return {
    name: str(data.name),
    manaCost: str(data.mana_cost),
    typeLine: str(data.type_line),
    power: str(data.power),
    toughness: str(data.toughness),
    loyalty: '',
    defense: '',
  };
}

/** Marken-Typ des Hauptwerts unten rechts: null, wenn die Seite Stärke/Widerstandskraft druckt; sonst 'loyalty', wenn sie Loyalität druckt; sonst 'defense', wenn sie Verteidigung druckt; sonst null. */
export function primaryCounter(card) {
  const face = visibleFace(card);
  if (face.power && face.toughness) return null;
  if (face.loyalty) return 'loyalty';
  if (face.defense) return 'defense';
  return null;
}

/**
 * `base` aus Stärke/Widerstandskraft der sichtbaren Seite (null, wenn einer der Werte fehlt);
 * `delta` = +1/+1 − −1/−1; `modified` nur, wenn beide Basiswerte ganze Zahlen sind — bei `*`
 * lässt sich nichts verrechnen, die Counter stehen dann als Badge daneben.
 */
export function computePowerToughness(card) {
  const counters = card?.counters || {};
  const delta = (counters['+1/+1'] || 0) - (counters['-1/-1'] || 0);
  const { power, toughness } = visibleFace(card);
  if (!power || !toughness) return { base: null, modified: null, delta };
  const base = { power, toughness };
  const modified = isInteger(power) && isInteger(toughness)
    ? { power: String(Number(power) + delta), toughness: String(Number(toughness) + delta) }
    : null;
  return { base, modified, delta };
}

/**
 * Badge-Einträge `{ key, kind, type, label, text, icon }`: erst Counter, dann Marken, je in der
 * Reihenfolge des Kontextmenüs, unbekannte Typen dahinter. +1/+1 und −1/−1 fehlen, sobald das
 * Kästchen sie eingerechnet zeigt, der Hauptwert, weil er unten rechts steht; `all: true`
 * (Werte-Panels) listet beide trotzdem, den Hauptwert bei fehlender Marke mit `text: '0'`.
 * `text` ist die Zahl eines Counters bzw. einer gezählten Marke, bei einfachen Marken leer.
 */
export function badgeEntries(card, { all = false } = {}) {
  const markers = card?.markers || {};
  const primary = primaryCounter(card);
  const counters = { ...(card?.counters || {}) };
  if (all && primary && !(counters[primary] > 0)) counters[primary] = 0;
  const ptInBox = !all && computePowerToughness(card).modified !== null;

  const counterEntries = ordered(counters, CARD_COUNTER_ICONS)
    .filter(([type, n]) => {
      if (type === primary) return all;
      return n > 0 && !(ptInBox && PT_COUNTERS.includes(type));
    })
    .map(([type, n, icon]) => ({
      key: `counter-${type}`,
      kind: 'counter',
      type,
      label: COUNTER_LABELS[type] || type,
      text: String(n),
      icon,
    }));
  const markerEntries = ordered(markers, CARD_MARKER_ICONS)
    .filter(([, value]) => value)
    .map(([type, value, icon]) => ({
      key: `marker-${type}`,
      kind: 'marker',
      label: MARKER_LABELS[type] || type,
      text: typeof value === 'number' && value > 1 ? String(value) : '',
      icon,
    }));
  return [...counterEntries, ...markerEntries];
}

/** Hat die Karte etwas für ein Werte-Panel (Stärke/Widerstandskraft, Counter oder Marken)? */
export function hasCardValues(card) {
  return computePowerToughness(card).base !== null || primaryCounter(card) !== null || badgeEntries(card, { all: true }).length > 0;
}

/** Text des Kästchens bzw. der Panel-Zeile: „2/2", mit Countern „2/2 → 4/4". */
export function formatPowerToughness({ base, modified, delta }, { withBase = false } = {}) {
  if (!base) return '';
  const baseText = `${base.power}/${base.toughness}`;
  if (!modified || delta === 0) return baseText;
  const modifiedText = `${modified.power}/${modified.toughness}`;
  return withBase ? `${baseText} → ${modifiedText}` : modifiedText;
}

function ordered(values, icons) {
  const known = icons.filter(([type]) => type in values).map(([type, icon]) => [type, values[type], icon]);
  const unknown = Object.keys(values)
    .filter((type) => !icons.some(([t]) => t === type))
    .map((type) => [type, values[type], Tag]);
  return [...known, ...unknown];
}
